import React, { ComponentType, useCallback, useEffect } from 'react';
import { actions } from '@brighte/brighte-one-core';
import isEmpty from 'lodash/isEmpty';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import Config from '../config';
import { isIdentity } from '../config/types';
import { cleanStoreLater } from '../utilities';
import { loadState } from '../utilities/localStorage';
import { persistIdentity, setAlertMessage } from '../actions';
import { refreshToken } from '../saga/api';
import { getAuthService } from '../AuthService';

const withTokenRefresh = <P extends object>(WrappedComponent: ComponentType<P>) => {
  const WithTokenRefresh = (props: any) => {
    const { identity = {}, needRefresh = false } = useSelector((state: Record<string, any>) => state.appData);
    const { setValidatePage } = actions;
    const dispatch = useDispatch();
    const localIdentity = loadState(Config.formId);

    useEffect(() => {
      // Admin sessions renew via oidc-client's silent (iframe) renew against Auth0; passwordless
      // sessions keep refreshing through ms-identification's REST endpoint, which already issues
      // Auth0 tokens for that flow.
      const renewAdminIdentity = async (): Promise<void> => {
        const user: Record<string, any> = await getAuthService().renewToken();
        const { id_token, access_token, refresh_token, scope } = user;
        // access_token is the credential the gateway validates; id_token is only a fallback if
        // Auth0 didn't issue an access token. Persist only the Identity fields we need rather
        // than spreading the full oidc-client user object (profile claims, expires_at,
        // session_state, ...) into localStorage.
        if (!(access_token || id_token)) {
          throw new Error('Silent renew did not return a token');
        }
        dispatch(
          persistIdentity({
            // Build from explicit fields rather than spreading ...identity — identity may itself
            // carry extra fields from an earlier persisted state, and we don't want to keep
            // re-persisting those forward on every renewal.
            identity: {
              accessToken: access_token || id_token,
              refreshToken: refresh_token || get(identity, 'refreshToken', ''),
              // oidc-client's User.scope is a space-separated string per the OIDC spec;
              // Identity.scope is typed as string[] — split on any whitespace run and drop empty
              // entries so extra/leading/trailing spaces don't leave blank items in the array.
              scope: scope ? scope.split(/\s+/).filter(Boolean) : get(identity, 'scope', []),
              provider: 'admin',
            },
            needRefresh: false,
          }),
        );
      };

      const renewPasswordlessIdentity = async (): Promise<void> => {
        const newIdentity = await refreshToken(get(identity, 'refreshToken', '')).then((response: Response) =>
          response.json(),
        );

        if (isIdentity(newIdentity)) {
          dispatch(persistIdentity({ identity: { ...newIdentity, provider: 'passwordless' }, needRefresh: false }));
        } else if (newIdentity.hasOwnProperty('message')) {
          throw new Error(newIdentity.message);
        } else {
          throw new Error('User identity not valid');
        }
      };

      const refreshTokenAction = async (): Promise<void> => {
        if (!isEmpty(identity) && needRefresh) {
          try {
            // Sessions persisted before this deploy predate the `provider` field entirely (both
            // flows used to share the same REST refresh path), so an unset provider is
            // genuinely ambiguous. Default to passwordless (the pre-existing behaviour for all
            // sessions) rather than admin: if an in-flight admin session hits this during the
            // deploy window, renewPasswordlessIdentity() fails fast with a clear "session
            // expired" prompt to re-login — safer than renewAdminIdentity() silently attempting
            // an oidc-client silent-iframe renew for a session that never went through
            // AuthService's signinRedirect() and has no corresponding Auth0 session to renew.
            if (identity.provider === 'admin') {
              await renewAdminIdentity();
            } else {
              await renewPasswordlessIdentity();
            }
          } catch (error) {
            console.warn('Error on retrieve identity.', error);
            dispatch(persistIdentity({ identity: {}, needRefresh: false }));
            dispatch(setAlertMessage('Session expired, please re-login.'));
            cleanStoreLater();
          }
        }
      };
      refreshTokenAction();
    }, [dispatch, identity, needRefresh, setValidatePage]);

    const fetchIdentity = useCallback((): void => {
      if (isEmpty(identity) && !isEmpty(localIdentity)) {
        dispatch(persistIdentity({ identity: localIdentity, needRefresh: true }));
      }
    }, [dispatch, identity, localIdentity]);

    return <WrappedComponent fetchLocalIdentity={fetchIdentity} localIdentity={localIdentity} {...props} />;
  };

  return WithTokenRefresh;
};

export default withTokenRefresh;
